import {
  ListChecks,
  ShoppingBasket,
  Timer,
  FileDown,
  BookmarkCheck,
} from "lucide-react";
import { lerp, easeOut, between } from "./useFrame";

type Props = { frame: number };

/**
 * 11–17s: Five feature cards slide through the frame, ~70 frames each, with
 * a brief overlap so the montage feels continuous rather than slideshow-like.
 *
 * Local frame range: 660..1020 (inside the parent's 0..1200).
 */
const FEATURES = [
  {
    Icon: ListChecks,
    title: "Build weekly menus",
    body: "Drop recipes into a plan in seconds.",
    visual: "menus" as const,
  },
  {
    Icon: ShoppingBasket,
    title: "Smart shopping lists",
    body: "Auto-merged & grouped by aisle.",
    visual: "list" as const,
  },
  {
    Icon: Timer,
    title: "Hands-free timers",
    body: "Tap any time in a step to start.",
    visual: "timer" as const,
  },
  {
    Icon: FileDown,
    title: "Export to PDF",
    body: "Save or print any recipe.",
    visual: "pdf" as const,
  },
  {
    Icon: BookmarkCheck,
    title: "Unlimited library",
    body: "Save as many as you like.",
    visual: "library" as const,
  },
];

const PER = 72; // frames per feature

export const ScenePremium = ({ frame }: Props) => {
  const f = frame - 660; // 0..360 local

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Premium pill */}
      <div
        className="absolute top-6 left-1/2 -translate-x-1/2"
        style={{
          opacity: easeOut(lerp(f, [0, 20], [0, 1])) * lerp(f, [330, 360], [1, 0]),
          transform: `translate(-50%, ${lerp(f, [0, 20], [-6, 0])}px)`,
        }}
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-mustard/20 px-3 py-1 text-[11px] font-semibold tracking-wide text-foreground ring-1 ring-mustard/40">
          <span className="h-1.5 w-1.5 rounded-full bg-mustard" />
          With Premium
        </span>
      </div>

      {FEATURES.map((feat, i) => {
        const start = i * PER;
        const end = start + PER;
        const visible = between(f, start, end + 8);
        if (!visible) return null;
        const enter = easeOut(lerp(f, [start, start + 18], [0, 1]));
        const exit = easeOut(lerp(f, [end - 18, end], [0, 1]));
        const opacity = enter * (1 - exit);
        const x = lerp(f, [start, start + 22], [60, 0]) + lerp(f, [end - 22, end], [0, -60]);
        const Icon = feat.Icon;
        return (
          <div
            key={i}
            className="absolute flex w-[78%] max-w-[420px] flex-col items-center gap-4"
            style={{ opacity, transform: `translateX(${x}px)` }}
          >
            <FeatureVisual kind={feat.visual} />
            <div className="text-center">
              <div className="mb-1.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <p className="font-display text-base font-semibold text-foreground sm:text-lg">
                {feat.title}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                {feat.body}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const FeatureVisual = ({ kind }: { kind: "menus" | "list" | "timer" | "pdf" | "library" }) => {
  if (kind === "menus") {
    return (
      <div className="grid w-full grid-cols-7 gap-1 rounded-xl border border-border bg-card p-2 shadow-card">
        {Array.from({ length: 21 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-md"
            style={{
              background:
                i % 3 === 0
                  ? "hsl(var(--primary) / 0.15)"
                  : i % 5 === 0
                  ? "hsl(var(--accent-basil) / 0.18)"
                  : "hsl(var(--muted))",
            }}
          />
        ))}
      </div>
    );
  }
  if (kind === "list") {
    return (
      <div className="w-full rounded-xl border border-border bg-card p-3 shadow-card">
        {[
          { dept: "Produce", items: ["Lemon", "Garlic", "Onion"] },
          { dept: "Meat", items: ["Chicken thighs"] },
          { dept: "Pantry", items: ["Olive oil", "Stock"] },
        ].map((g) => (
          <div key={g.dept} className="mb-2 last:mb-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {g.dept}
            </p>
            {g.items.map((it) => (
              <p key={it} className="text-xs text-foreground">
                ☐ {it}
              </p>
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (kind === "timer") {
    return (
      <div className="flex w-full flex-col items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-card">
        <p className="text-xs text-muted-foreground">
          Roast for <span className="rounded bg-primary/15 px-1.5 py-0.5 font-semibold text-primary">20 minutes</span>
        </p>
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-primary/30 font-display text-lg font-semibold text-primary">
            19:42
          </div>
          <div className="text-left text-[11px] text-muted-foreground">
            <p className="font-semibold text-foreground">Roasting chicken</p>
            <p>Screen stays awake</p>
          </div>
        </div>
      </div>
    );
  }
  if (kind === "pdf") {
    return (
      <div className="relative w-full">
        <div className="mx-auto w-[60%] rounded-md border border-border bg-card p-3 shadow-lift">
          <div className="mb-1.5 h-2 w-2/3 rounded bg-foreground/70" />
          <div className="mb-1 h-1.5 w-1/2 rounded bg-muted-foreground/60" />
          <div className="mt-2 space-y-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-1 w-full rounded bg-muted" />
            ))}
          </div>
          <div className="mt-2 flex items-center gap-1 text-[8px] font-semibold text-primary">
            <FileDown className="h-2.5 w-2.5" /> recipe.pdf
          </div>
        </div>
      </div>
    );
  }
  // library
  return (
    <div className="grid w-full grid-cols-3 gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="aspect-[4/5] rounded-lg border border-border bg-card p-2 shadow-card"
        >
          <div className="mb-1.5 h-1/2 rounded bg-muted" />
          <div className="mb-1 h-1.5 w-3/4 rounded bg-foreground/60" />
          <div className="h-1 w-1/2 rounded bg-muted-foreground/50" />
        </div>
      ))}
    </div>
  );
};
